import React, {useCallback, useEffect, useMemo, useState} from 'react';
import Dialog from "../../../base/dialog";
import {Box, Card, DialogActions, DialogContent, DialogTitle, Divider, formLabelClasses, IconButton, inputAdornmentClasses, outlinedInputClasses, Typography} from "@mui/material";
import Button from "../../../base/button/button";
import Form, {MuiRFF, Yup} from "../../../form";
import Grid2 from "@mui/material/Unstable_Grid2";
import {AddCircleOutline, BusinessOutlined, DeleteOutlineOutlined, Inventory2Outlined, LocalShippingOutlined} from "@mui/icons-material";
import DateUtils from "../../../../../core/services/date-utils";
import ValidateMessages from "../../../../../core/models/static/texts/vallidate-messages";
import {Tab, Tabs} from "../../../base/Tabs";
import {makeRequired, makeValidate} from "mui-rff";
import {ReactComponent as CheckboxIcon} from "../../../../../assets/images/check-box/checkbox.svg";
import {ReactComponent as ActiveCheckboxIcon} from "../../../../../assets/images/check-box/checkbox-active.svg";
import SaveOrRollback from "../../save-or-rollback";
import {SCHEDULE_TYPE_IDS, WEEKDAY_KEYS} from "./schedule-utils";
import Api from "../../../../../core/services/api/api";
import {MuiRFFTimePicker} from "../../../../custom/mui_rff/TimePicker";
import {deepEqual} from "../../../../../core/services/utils/utils";
import {MuiRFFDatePicker} from "../../../../custom/mui_rff/DatePicker";


const WarehouseScheduleManagementDialog = ({
                                               warehouse,
                                               open,
                                               close,
                                               schedule,
                                               selectedScheduleId,
                                               saveSchedule: onUpdateSchedule
                                           }) => {
    const [activeTab, setActiveTab] = useState(false);

    const WEEKDAYS = WEEKDAY_KEYS;
    const scheduleTypes = React.useMemo(() => [
        {id: SCHEDULE_TYPE_IDS.TAKING_ORDER, icon: <BusinessOutlined color={'primary'}/>, title: 'Business Hours'},
        {id: SCHEDULE_TYPE_IDS.DELIVERY, icon: <LocalShippingOutlined color={'primary'}/>, title: 'Delivery'},
        {id: SCHEDULE_TYPE_IDS.PICKUP, icon: <Inventory2Outlined color={'primary'}/>, title: 'Pickup'},
    ], []);

    /**
     * Set the active tab when selectedScheduleId changes and dialog opens
     */
    useEffect(() => {
        if (selectedScheduleId && open) {
            setActiveTab(selectedScheduleId);
        }
    }, [selectedScheduleId, open]);


    const initialValues = useMemo(() => {
        const initial = {};
        const todayStr = DateUtils.serverNow().format('YYYY-MM-DD');

        const toDate = (timeStr) => {
            if (!timeStr) return null;
            return DateUtils.server(`${todayStr}T${timeStr}`);
        };

        for (const type of scheduleTypes) {
            const item = schedule?.find(s => s.scheduleId === type.id);
            // Weekly
            initial[type.title] = {};
            for (const day of WEEKDAYS) {
                const times = item?.times?.[day];
                if (times) {
                    if (times.startTime === null && times.endTime === null) {
                        initial[type.title][day] = {
                            id: times.id,
                            startTime: null,
                            endTime: null,
                            closed: true,
                        };
                    } else {
                        initial[type.title][day] = {
                            id: times.id,
                            startTime: toDate(times.startTime),
                            endTime: toDate(times.endTime),
                            closed: false
                        };
                    }
                } else {
                    initial[type.title][day] = {
                        id: null,
                        startTime: null,
                        endTime: null,
                        closed: false
                    };
                }
            }

            // Exceptions
            initial[type.title].exceptions = item?.exceptions?.map(ex => ({
                id: ex.id,
                date: ex.date ? DateUtils.server(ex.date) : null,
                startTime: toDate(ex.startTime),
                endTime: toDate(ex.endTime),
                closed: ex.closed || (ex.startTime === null && ex.endTime === null)
            })) || [];
        }
        return initial;
    }, [WEEKDAYS, schedule, scheduleTypes]);

    return (
        <Dialog
            open={open}
            setOpen={close}
            maxWidth="md"
            fullWidth
            className={'bound-dialog warehouse-schedule-management-dialog'}
            slotProps={{root: {sx: {zIndex: theme => theme.zIndex.modal + 2}}}}
        >
            <DialogTitle sx={{display: 'flex', flexDirection: 'column', gap: 1}} component={'div'}>
                <Typography variant="h6" className={'p-3'}>
                    Manage Schedule of {warehouse?.name ?? ""}
                </Typography>
                <Divider flexItem orientation="horizontal"/>
                <Tabs value={activeTab ?? false} onChange={(e, v) => setActiveTab(v)} variant="fullWidth">
                    {
                        scheduleTypes.map(type => (
                            <Tab
                                key={type.id}
                                value={type.id}
                                label={
                                    <Box sx={{display: 'flex', alignItems: 'center', gap: 1, color: 'inherit'}}>
                                        {type.icon}
                                        {type.title}
                                    </Box>
                                }
                            />
                        ))
                    }
                </Tabs>
            </DialogTitle>
            <DialogContent dividers sx={{borderTop: 'none'}}>
                {
                    scheduleTypes.map(type => (
                        <Box key={type.id} role="tabpanel" hidden={activeTab !== type.id} sx={{p: 2, overflowY: 'auto', flexGrow: 1}}>
                            <Typography variant="subtitle1" gutterBottom sx={{fontWeight: 600, marginBlockEnd: 2}}>
                                Weekly Schedule
                            </Typography>
                            <Grid2 container gap={2} sx={{mb: 3}}>
                                {
                                    WEEKDAYS.map(day => (
                                        <WeeklyDayRow
                                            key={day}
                                            type={type.title}
                                            typeId={type.id}
                                            day={day}
                                            initialValues={initialValues}
                                            warehouse={warehouse}
                                            onUpdateSchedule={onUpdateSchedule}
                                        />
                                    ))
                                }
                            </Grid2>
                            <Typography variant="subtitle1" gutterBottom sx={{fontWeight: 600, marginBlockEnd: 2}}>
                                Schedule Exceptions
                            </Typography>
                            <ExceptionsList
                                type={type.title}
                                typeId={type.id}
                                initialValues={initialValues}
                                warehouse={warehouse}
                                onUpdateSchedule={onUpdateSchedule}
                            />
                        </Box>
                    ))
                }
            </DialogContent>
            <DialogActions>
                <Button
                    disabled={!open}
                    variant={'dialog'}
                    color={'primary'}
                    onClick={close}
                    borderRadius={'8px 0 8px 0'}
                >
                    Close
                </Button>
            </DialogActions>
        </Dialog>
    );
};

/**
 * Convert dayjs objects to "HH:mm" strings for API conversion
 * @param dateObj
 * @returns {string|null}
 */
const toTimeStr = (dateObj) => {
    if (!dateObj) return null;
    return DateUtils.server(dateObj).format('HH:mm:ss');
};

/**
 * Convert dayjs date to "YYYY-MM-DD"
 * @param dateObj
 * @returns {string|null}
 */
const toDateStr = (dateObj) => {
    if (!dateObj) return null;
    return DateUtils.server(dateObj).format(DateUtils.withoutOffsetFormat);
}

const WeeklyDayRow = ({type, typeId, day, initialValues, warehouse, onUpdateSchedule}) => {
    const schema = useMemo(() => {
        return Yup.object().shape({
            closed: Yup.boolean(),
            startTime: Yup.mixed().when('closed', {
                is: false,
                then: Yup.mixed().required(ValidateMessages.required)
            }),
            endTime: Yup.mixed().when('closed', {
                is: false,
                then: Yup.mixed().required(ValidateMessages.required)
                    .test('is-after-start', 'End time must be after start time', function (value) {
                        const {startTime} = this.parent;
                        if (!startTime || !value) return true;
                        return DateUtils.server(value).isAfter(DateUtils.server(startTime));
                    })
            })
        });
    }, []);

    const validate = useMemo(() => makeValidate(schema), [schema]);
    const required = useMemo(() => makeRequired(schema), [schema]);

    const rowInitialValues = useMemo(() => initialValues?.[type]?.[day] || {}, [initialValues, type, day]);

    /**
     * Handle save (update) of weekly day
     * @type {(function(*): Promise<void>)}
     */
    const handleSave = useCallback(async (values) => {
        const apiPayload = {
            id: rowInitialValues.id, // null for new
            warehouseId: warehouse.id,
            typeId: typeId,
            dayOfWeek: WEEKDAY_KEYS.indexOf(day),
            specialDate: null,
            startTime: values.closed ? null : toTimeStr(values.startTime),
            endTime: values.closed ? null : toTimeStr(values.endTime),
        };
        const response = await Api.setPartnerOperationHours(apiPayload);
        if (response?.resultFlag) {
            onUpdateSchedule({
                ...response.data,
            });
        }
        return response?.resultFlag ?? false;
    }, [day, onUpdateSchedule, rowInitialValues.id, typeId, warehouse.id])

    return (
        <Form
            onSubmit={handleSave}
            initialValues={rowInitialValues}
            validate={validate}
            render={({submitting, initialValues, values, form}) => {
                const isClosed = values?.closed;
                const isDirty = !deepEqual(values, initialValues);
                return (
                    <>
                        <Grid2 container xs={12} spacing={2} alignItems="center">
                            <Grid2 xs={'auto'}>
                                <Typography
                                    variant="subtitle2"
                                    sx={{
                                        textTransform: 'capitalize',
                                        width: 50,
                                        textAlign: 'center',
                                        backgroundColor: theme => `rgba(${theme.vars.palette.primary.mainChannel} / 0.1)`,
                                        p: 1,
                                        borderRadius: 1
                                    }}
                                >
                                    {day}
                                </Typography>
                            </Grid2>
                            <Grid2 xs={'auto'}>
                                <MuiRFFTimePicker
                                    name={`startTime`}
                                    label="Start Time"
                                    ampm={false}
                                    disabled={isClosed}
                                    required={!isClosed && required[`startTime`]}
                                    textFieldProps={{size: 'small', sx: {width: 200}}}
                                    slotProps={{
                                        popper: {sx: {zIndex: theme => theme.zIndex.modal + 3}},
                                        actionBar: {actions: ['accept', 'cancel', 'clear']},
                                    }}
                                />
                            </Grid2>
                            <Grid2 xs={'auto'}>
                                <MuiRFFTimePicker
                                    name={`endTime`}
                                    label="End Time"
                                    ampm={false}
                                    disabled={isClosed}
                                    required={!isClosed && required[`endTime`]}
                                    textFieldProps={{size: 'small', sx: {width: 200}}}
                                    slotProps={{
                                        popper: {sx: {zIndex: theme => theme.zIndex.modal + 3}},
                                        actionBar: {actions: ['accept', 'cancel', 'clear']},
                                    }}
                                />
                            </Grid2>
                            <Grid2 xs={'auto'}>
                                <MuiRFF.Checkboxes
                                    name={`closed`}
                                    data={{label: 'Closed', value: true}}
                                    size={"small"}
                                    required={required[`closed`]}
                                    icon={<CheckboxIcon/>}
                                    checkedIcon={<ActiveCheckboxIcon/>}
                                    formControlLabelProps={{
                                        sx: {margin: 0,}
                                    }}
                                />
                            </Grid2>
                            <Grid2 xs={true}>
                                <SaveOrRollback
                                    column={false}
                                    loading={submitting}
                                    show={isDirty}
                                    onSave={() => form.submit()}
                                    onRollback={() => form.reset()}
                                />
                            </Grid2>
                        </Grid2>
                    </>
                )
            }}
        />
    )
}

const ExceptionsList = ({type, typeId, initialValues, warehouse, onUpdateSchedule}) => {
    const existingExceptions = useMemo(() => initialValues?.[type]?.exceptions || [], [initialValues, type]);
    const [newExceptions, setNewExceptions] = useState([]);

    /**
     * Check if exception date is unique among existing exceptions
     * @type {(function(*, *): (boolean|*))|*}
     */
    const isUniqueTest = useCallback((exceptionId, date) => {
        if (!existingExceptions) return true;
        return !existingExceptions.some(ex => ex.id !== exceptionId && DateUtils.server(ex.date).isSame(DateUtils.server(date), 'day'));
    }, [existingExceptions]);

    /**
     * Add a new exception row
     */
    const addException = useCallback(() => {
        setNewExceptions(prev => [
            ...prev,
            {_tempId: Date.now(), date: null, startTime: null, endTime: null, closed: false}
        ]);
    }, []);

    /**
     * Remove an existing exception (call API)
     */
    const removeExisting = useCallback(async (id) => {
        const response = await Api.removePartnerOperationHours(id);
        if (response && response.resultFlag) {
            onUpdateSchedule({id}, true);
        }
    }, [onUpdateSchedule]);

    /**
     * Remove a new (unsaved) exception
     */
    const removeNew = useCallback((tempId) => {
        setNewExceptions(prev => prev.filter(item => item._tempId !== tempId));
    }, []);

    return (
        <Box>
            {
                existingExceptions.map((ex, index) => (
                    <ExceptionRow
                        key={ex.id || index}
                        typeId={typeId}
                        initialValues={ex}
                        isUniqueTest={isUniqueTest}
                        warehouse={warehouse}
                        onUpdateSchedule={onUpdateSchedule}
                        onRemove={() => removeExisting(ex.id)}
                        isNew={false}
                    />
                ))
            }
            {
                newExceptions.map((ex) => (
                    <ExceptionRow
                        key={ex._tempId}
                        typeId={typeId}
                        isUniqueTest={isUniqueTest}
                        initialValues={ex}
                        warehouse={warehouse}
                        onUpdateSchedule={onUpdateSchedule}
                        onRemove={() => removeNew(ex._tempId)}
                        isNew={true}
                    />
                ))
            }
            <Button
                startIcon={<AddCircleOutline/>}
                onClick={addException}
                variant="outlined"
                color="primary"
                sx={{mt: 1}}
            >
                Add Exception
            </Button>
        </Box>
    );
};

const ExceptionRow = ({typeId, initialValues, warehouse, onUpdateSchedule, onRemove, isNew, isUniqueTest}) => {
    const schema = useMemo(() => {
        return Yup.object().shape({
            date: Yup.mixed().required(ValidateMessages.required)
                .validDate(ValidateMessages.incorrect)
                .test('is-unique-among-exceptions', 'Date must be unique among exceptions', function (value) {
                    const {id} = this.parent;
                    if (!value) return true;
                    return isUniqueTest(id, value);
                }),
            closed: Yup.boolean(),
            startTime: Yup.mixed().when('closed', {
                is: false,
                then: Yup.mixed().required(ValidateMessages.required),
            }),
            endTime: Yup.mixed().when('closed', {
                is: false,
                then: Yup.mixed().required(ValidateMessages.required)
                    .test('is-after-start', 'End time must be after start time', function (value) {
                        const {startTime} = this.parent;
                        if (!startTime || !value) return true;
                        return DateUtils.server(value).isAfter(DateUtils.server(startTime));
                    })
            })
        });
    }, [isUniqueTest]);

    const validate = useMemo(() => makeValidate(schema), [schema]);
    const required = useMemo(() => makeRequired(schema), [schema]);

    /**
     * Handle save (create or update) of exception
     * @param values
     * @param form
     * @returns {Promise<void>}
     */
    const handleSave = useCallback(async (values, form) => {
        const apiPayload = {
            id: values.id, // null for new
            warehouseId: warehouse.id,
            typeId: typeId,
            dayOfWeek: null,
            specialDate: toDateStr(values.date),
            startTime: values.closed ? null : toTimeStr(values.startTime),
            endTime: values.closed ? null : toTimeStr(values.endTime),
        };
        const response = await Api.setPartnerOperationHours(apiPayload);
        if (response?.resultFlag) {
            onUpdateSchedule(response.data);
            if (isNew) {
                onRemove();
            } else {
                form.reset(values);
            }
        }
        return response?.resultFlag ?? false;
    }, [warehouse, typeId, onUpdateSchedule, isNew, onRemove]);

    return (
        <Form
            onSubmit={handleSave}
            initialValues={initialValues}
            validate={validate}
            render={({values, form, pristine, submitting}) => {
                const isClosed = values?.closed;
                const isDirty = !pristine;
                return (
                    <Card variant="outlined" sx={{p: 1, mb: 2}}>
                        <Grid2 container spacing={2} alignItems="center">
                            <Grid2 xs={'auto'}>
                                <MuiRFFDatePicker
                                    readOnly={!!values.id}
                                    name={`date`}
                                    label="Date"
                                    required={required[`date`]}
                                    disablePast
                                    textFieldProps={{
                                        size: 'small',
                                        sx: {
                                            width: 200,
                                            ...(!!values.id && {
                                                [`.${outlinedInputClasses.notchedOutline}`]: {
                                                    borderColor: 'transparent'
                                                },
                                                [`.${inputAdornmentClasses.positionEnd}`]: {
                                                    display: 'none',
                                                },
                                                [`.${formLabelClasses.root}`]: {
                                                    display: 'none',
                                                }
                                            }),
                                        },
                                    }}
                                    slotProps={{
                                        popper: {sx: {zIndex: theme => theme.zIndex.modal + 3}},
                                    }}
                                />
                            </Grid2>
                            <Grid2 xs={'auto'}>
                                <MuiRFFTimePicker
                                    name={`startTime`}
                                    label="Start Time"
                                    ampm={false}
                                    disabled={isClosed}
                                    required={!isClosed && required[`startTime`]}
                                    textFieldProps={{size: 'small', sx: {width: 150}}}
                                    slotProps={{
                                        popper: {sx: {zIndex: theme => theme.zIndex.modal + 3}},
                                        actionBar: {actions: ['accept', 'cancel', 'clear']},
                                    }}
                                />
                            </Grid2>
                            <Grid2 xs={'auto'}>
                                <MuiRFFTimePicker
                                    name={`endTime`}
                                    label="End Time"
                                    ampm={false}
                                    disabled={isClosed}
                                    required={!isClosed && required[`endTime`]}
                                    textFieldProps={{size: 'small', sx: {width: 150}}}
                                    slotProps={{
                                        popper: {sx: {zIndex: theme => theme.zIndex.modal + 3}},
                                        actionBar: {actions: ['accept', 'cancel', 'clear']},
                                    }}
                                />
                            </Grid2>
                            <Grid2 xs={'auto'}>
                                <MuiRFF.Checkboxes
                                    name={`closed`}
                                    data={{label: 'Closed', value: true}}
                                    size={"small"}
                                    icon={<CheckboxIcon/>}
                                    checkedIcon={<ActiveCheckboxIcon/>}
                                    formControlLabelProps={{sx: {margin: 0}}}
                                />
                            </Grid2>
                            <Grid2 xs={true}>
                                <Box sx={{display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 1}}>
                                    <SaveOrRollback
                                        column={false}
                                        show={isDirty || isNew}
                                        onSave={() => form.submit()}
                                        loading={submitting}
                                        onRollback={() => {
                                            if (isNew) onRemove();
                                            else form.restart(initialValues);
                                        }}
                                    />
                                    {
                                        (!isDirty && !isNew) &&
                                        <IconButton onClick={onRemove} color="error" size="small">
                                            <DeleteOutlineOutlined/>
                                        </IconButton>
                                    }
                                </Box>
                            </Grid2>
                        </Grid2>
                    </Card>
                );
            }}
        />
    );
};

export default WarehouseScheduleManagementDialog;
